import React, { useContext, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { MODULES, SPECIAL, MODULE_BY_KEY } from "../modules";
import { PermsContext } from "../perms";
import { clearToken } from "../auth";

// Tenant menu (Phase 3). Items are module keys (/m/<key>) or special-page keys.
// Anything registered in modules.js but not listed here lands in "More", so no screen is ever orphaned.
const MENU = [
  ["Discovery", ["estate", "machines", "estate-scans", "findings", "unencrypted", "agent-servers", "pii-map", "discovery", "discovery-scans", "classification-rules"]],
  ["Data Inventory", ["repositories", "databases", "applications", "data-categories", "data-fields", "ropa"]],
  ["Consent & Rights", ["consent-purposes", "consent-register", "consent-templates", "bulk-consent", "cookie-categories", "cookie-consents", "rights", "grievances"]],
  ["Risk & Assessments", ["risks", "pia", "breaches"]],
  ["Governance", ["compliance", "obligations", "controls", "checklist", "policies", "vendors", "data-sharing", "retention", "calendar-view", "calendar"]],
  ["Reports", ["reports", "report-schedules"]],
  ["Administration", ["roles", "user-roles", "workflow", "support-access", "workflow-config", "notifications", "audit-log"]],
];

function resolve(key) {
  const m = MODULE_BY_KEY[key];
  if (m) return { key, title: m.title, to: `/m/${key}` };
  const sp = SPECIAL.find((x) => x.key === key);
  return sp ? { key, title: sp.title, to: sp.path, special: true } : null;
}

function buildGroups() {
  const used = new Set();
  const groups = MENU.map(([g, keys]) => [g, keys.map(resolve).filter(Boolean)]).filter(([, items]) => items.length);
  groups.forEach(([, items]) => items.forEach((i) => used.add(i.key)));
  const more = [...MODULES.map((m) => m.key), ...SPECIAL.map((x) => x.key)].filter((k) => !used.has(k)).map(resolve);
  if (more.length) groups.push(["More", more]);
  return groups;
}

export default function Layout({ children }) {
  const navigate = useNavigate();
  const { role, tenant, support, supportExpires } = useContext(PermsContext);
  const { pathname } = useLocation();
  const groups = buildGroups();
  const [open, setOpen] = useState({});
  const hasActive = (items) => items.some((i) => (i.special ? pathname === i.to : pathname.startsWith(i.to)));
  const logout = () => { clearToken(); navigate("/login"); };
  return (
    <div className="app">
      <aside className="side">
        <div className="brand">Vxaro DPDP</div>
        {tenant && <div className="tenant">{tenant.name}</div>}
        {/* Scrolls independently of the page — the nav is taller than the viewport. */}
        <nav className="sidenav">
          <NavLink to="/" end className={({ isActive }) => (isActive ? "active" : "")}>Dashboard</NavLink>
          {groups.map(([group, items]) => {
            const isOpen = open[group] ?? hasActive(items);
            return (
              <div key={group}>
                <div className="grp toggle" onClick={() => setOpen({ ...open, [group]: !isOpen })}>{group}<span>{isOpen ? "▾" : "▸"}</span></div>
                {isOpen && items.map((it) => (
                  <NavLink key={it.key} to={it.to} end={it.special} className={({ isActive }) => (isActive ? "active" : "")}>{it.title}</NavLink>
                ))}
              </div>
            );
          })}
        </nav>
      </aside>
      <div className="main">
        <div className="top">
          <div style={{ fontWeight: 600 }}>{tenant ? tenant.name : "DPDP Compliance Management"}</div>
          <div className="who">
            {role && <span className="pill" style={{ marginRight: 10 }}>{role}</span>}
            <button className="btn sm" onClick={logout}>Logout</button>
          </div>
        </div>
        <div className="content">{support && <div className="err" style={{ marginBottom: 10, fontWeight: 600 }}>Vxaro support session — read-only. Every page you open is recorded in this tenant's audit log.{supportExpires ? ` Access ends ${new Date(supportExpires).toLocaleString()}.` : ""}</div>}{children}</div>
      </div>
    </div>
  );
}
